import useApi, { useFetchApi } from "../services/ApiService";
import {
  ConflictRuleCatalog,
  ConflictRuleLibrary,
  ConflictRuleOutcome,
  ConflictRulePreview,
  ConflictRuleProgress,
  ConflictRuleRunGroups,
  ConflictRuleRunResult,
  ConflictRuleValidation,
  RuleDiagnostic,
  SavedConflictRule,
} from "../models/ConflictRule";
import { extractApiErrorMessage, extractProblemDetailsMessage } from "../lib/apiError";
import { readNdjsonResult } from "../lib/ndjson";
import { saveResponseAsFile } from "../lib/download";

/**
 * A conflict-rule request the server refused before doing anything. A rule the server finds errors in comes back as
 * a 400 whose failures are keyed by JSON pointer, the same diagnostics `Validate` returns, so the form can pin them.
 */
export class ConflictRuleRequestError extends Error {
  readonly status: number;
  readonly diagnostics: RuleDiagnostic[];

  constructor(status: number, message: string, diagnostics: RuleDiagnostic[]) {
    super(message);
    this.name = "ConflictRuleRequestError";
    this.status = status;
    this.diagnostics = diagnostics;
  }
}

const refusal = async (response: Response, fallback: string): Promise<ConflictRuleRequestError> => {
  const payload = await response.json().catch(() => null);
  const diagnostics: RuleDiagnostic[] = [];
  if (payload?.errors && typeof payload.errors === "object") {
    for (const [pointer, messages] of Object.entries(payload.errors)) {
      if (!pointer.startsWith("/") || !Array.isArray(messages)) continue;
      for (const message of messages) if (typeof message === "string") diagnostics.push({ pointer, message });
    }
  }
  return new ConflictRuleRequestError(response.status, extractProblemDetailsMessage(payload, fallback), diagnostics);
};

/**
 * Conflict rules: the vocabulary and validation the rule form uses, the shared library, previews, runs, and a past
 * run's trail and CSV. A rule is always sent as its JSON text, never as an object, so what is previewed, run, saved and
 * audited is the same text.
 */
export class ConflictRulesRepository {
  private api = useApi();
  private fetchApi = useFetchApi();

  async getCatalog(): Promise<ConflictRuleCatalog> {
    try {
      return (await this.api.get<ConflictRuleCatalog>("/api/ConflictRules/Catalog")).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load the rule vocabulary"));
    }
  }

  /** Every error and warning in the rule. An invalid rule still answers 200. */
  async validate(rule: string, signal?: AbortSignal): Promise<ConflictRuleValidation> {
    try {
      return (await this.api.post<ConflictRuleValidation>("/api/ConflictRules/Validate", { rule }, { signal })).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to check the rule"));
    }
  }

  async getLibrary(): Promise<ConflictRuleLibrary> {
    try {
      return (await this.api.get<ConflictRuleLibrary>("/api/ConflictRules/Library")).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load the rule library"));
    }
  }

  /** Saves the rule under its own name. Refused when it has errors, no name, or a name already in the library. */
  async createRule(definition: string, description: string | null): Promise<SavedConflictRule> {
    try {
      return (await this.api.post<SavedConflictRule>("/api/ConflictRules/Library", { definition, description })).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to save the rule"));
    }
  }

  /** Saves a new version. Refused when someone saved the rule again after `version`. */
  async updateRule(
    id: string,
    definition: string,
    description: string | null,
    version: number
  ): Promise<SavedConflictRule> {
    try {
      return (
        await this.api.put<SavedConflictRule>(`/api/ConflictRules/Library/${id}`, { definition, description, version })
      ).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to save the rule"));
    }
  }

  async deleteRule(id: string): Promise<void> {
    try {
      await this.api.delete(`/api/ConflictRules/Library/${id}`);
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to delete the rule"));
    }
  }

  /** What the rule would decide and skip now. Writes nothing. */
  async preview(
    rule: string,
    onProgress?: (progress: ConflictRuleProgress) => void,
    signal?: AbortSignal
  ): Promise<ConflictRulePreview> {
    const response = await this.fetchApi("/api/ConflictRules/Preview", { method: "POST", body: { rule }, signal });
    if (!response.ok) throw await refusal(response, "Failed to preview the rule");

    return readNdjsonResult<ConflictRuleProgress, ConflictRulePreview>(response, {
      onProgress,
      errorMessage: "The preview failed after it had started.",
      droppedMessage: "The connection dropped before the preview finished. Nothing was changed: preview again.",
    });
  }

  /**
   * Runs the rule. A 409 means it no longer decides `confirmedFileCount` files and nothing was changed. Once the first
   * chunk commits, a failure or a dropped connection keeps what committed: the run's audit entry records it.
   */
  async execute(
    request: { rule: string; reason: string; confirmedFileCount: number },
    onProgress?: (progress: ConflictRuleProgress) => void,
    signal?: AbortSignal
  ): Promise<ConflictRuleRunResult> {
    const response = await this.fetchApi("/api/ConflictRules/Execute", { method: "POST", body: request, signal });
    if (!response.ok) throw await refusal(response, "Failed to run the rule");

    return readNdjsonResult<ConflictRuleProgress, ConflictRuleRunResult>(response, {
      onProgress,
      errorMessage: "The run failed after it had started.",
      droppedMessage: "The connection dropped before the run finished.",
    });
  }

  /** Downloads what the rule would decide and skip now, as a CSV. The download is recorded in the audit log. */
  async downloadPreviewCsv(rule: string): Promise<void> {
    const response = await this.fetchApi("/api/ConflictRules/Preview/Export", { method: "POST", body: { rule } });
    if (!response.ok) throw await refusal(response, "Failed to download the preview CSV");
    await saveResponseAsFile(response, "conflict-rule-preview.csv");
  }

  /** A page of one run's groups, read from the snapshot the run wrote to its audit entry. */
  async getRunGroups(
    auditLogId: string,
    outcome: ConflictRuleOutcome | null,
    check: string | null,
    pageNumber: number,
    pageSize: number
  ): Promise<ConflictRuleRunGroups> {
    try {
      return (
        await this.api.get<ConflictRuleRunGroups>(`/api/ConflictRules/Runs/${auditLogId}/Groups`, {
          params: { outcome, check, pageNumber, pageSize },
        })
      ).data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to load the run's groups"));
    }
  }

  /** Downloads one run's trail as a CSV. The download is recorded in the audit log. */
  async downloadRunCsv(auditLogId: string): Promise<void> {
    const response = await this.fetchApi(`/api/ConflictRules/Runs/${auditLogId}/Export`, { method: "GET" });
    if (!response.ok) throw await refusal(response, "Failed to download the run CSV");
    await saveResponseAsFile(response, `conflict-rule-run-${auditLogId}.csv`);
  }
}

export default ConflictRulesRepository;
